import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Input,
  Button,
} from "@mui/joy";
import { Paper, Dialog } from "@mui/material";
import { FileDownload, PictureAsPdf } from "@mui/icons-material";
import { Card, Row } from "react-bootstrap";
import { connect } from "react-redux";
import {
  GET_PAYOUTS_HISTORY_API_URL,
  REQUEST_PAYOUT_API_URL,
} from "../../configs/url";
import apiCall from "../../helpers/api";
import { flatten } from "../../helpers/api";
import { updateUserInfo } from "../../services/user/actions";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  datatable_style,
  downloadTablePdf,
  exportToCsv,
  LoadingComponent,
  NoDataComponent,
} from "../../helpers/datatable_helpers";
import CustomDateRangePicker from "../../helpers/CustomDateRangePicker";
import { useTranslation } from "react-i18next";
import { min } from "date-fns";

const Loading = () => (
  <Box className="loading-overlay">
    <CircularProgress size="lg" />
  </Box>
);

const Payouts = (props) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawDisabled, setWithdrawDisabled] = useState(true);
  const [filteredRows, setFilteredRows] = useState([]);
  const [fullLoading, setFullLoading] = useState(false);
  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
    key: "selection",
  });

  const { enqueueSnackbar } = useSnackbar();
  const { settings } = props;
  const { t } = useTranslation();

  const dataColumns = [
    {
      data: "id",
      name: "id",
      title: t("payouts_column_id"),
      searchable: true,
    },
    {
      data: "created_at",
      name: "created_at",
      searchable: true,
      title: t("payouts_column_date"),
    },
    {
      data: "amount",
      name: "amount",
      searchable: true,
      title: t("payouts_column_amount"),
    },
    {
      data: "transaction_mode",
      name: "transaction_mode",
      title: t("payouts_column_transaction_mode"),
      searchable: true,
    },
    {
      data: "transaction_id",
      name: "transaction_id",
      searchable: true,
      title: t("payouts_column_transaction_id"),
    },
    {
      data: "transaction_message",
      name: "transaction_message",
      title: t("payouts_column_transaction_message"),
      searchable: true,
    },
    {
      data: "status",
      name: "status",
      title: t("payouts_column_status"),
      searchable: true,
    },
  ];

  useEffect(() => {
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    document.title = t("sidebar_payouts") + " | KPG Partner";
  }, [props?.user]);

  const fetchData = async () => {
    setLoading(true);
    const data = {
      draw: 1,
      start: (page - 1) * perPage,
      length: perPage,
      search: { value: search },
      columns: dataColumns,
    };
    try {
      const res = await apiCall({
        method: "POST",
        url:
          GET_PAYOUTS_HISTORY_API_URL +
          "?" +
          new URLSearchParams(flatten(data)),
        data: { start_date: dates.startDate, end_date: dates.endDate },
      });

      const processedData = res.data.data.map((row) => ({
        id: row.id || "",
        created_at: row.created_at || "",
        amount: Number(row.amount) || 0,
        transaction_mode: row.transaction_mode || "",
        transaction_id: row.transaction_id || "",
        transaction_message: row.transaction_message || "",
        status: row.status || "",
      }));

      setRows(processedData);
      setFilteredRows(processedData);
      setTotal(res.data.recordsFiltered);
    } catch (err) {
      console.error("Error fetching payouts:", err);
      setRows([]);
      setFilteredRows([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleWithdraw = async () => {
    setFullLoading(true);
    setWithdrawModalOpen(false);
    try {
      const response = await apiCall({
        method: "POST",
        url: REQUEST_PAYOUT_API_URL,
      });

      if (response.status === 200) {
        fetchData();
        enqueueSnackbar("Payout requested successfully!", {
          variant: "success",
        });
        props?.updateUserInfo();
      } else {
        console.error("Failed to request payout:", response.data);
        enqueueSnackbar("Failed to request payout", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error requesting payout:", error);
      enqueueSnackbar("Failed to request payout", {
        variant: "error",
      });
    }
    setFullLoading(false);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearch(searchValue);
    setPage(1);
  };

  const handleApplyDateRange = (startDate, endDate) => {
    fetchData();
  };

  useEffect(() => {
    if (props?.user?.auth_token) {
      fetchData();
    }
  }, [page, props?.user, search, perPage]);

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "QUEUED":
        return "primary";
      case "PROCESSING":
        return "primary";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "danger";
      case "REJECTED":
        return "danger";
      default:
        return "neutral";
    }
  };

  const columns = useMemo(
    () => [
      {
        name: t("payouts_column_id"),
        selector: (row) => row.id,
        sortable: true,
        minWidth: "10px",
        cell: (row) => (
          <Typography level="body-sm" className="cell-id">
            {"#" + row.id || "N/A"}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_date"),
        selector: (row) => row.created_at,
        sortable: true,
        minWidth: "170px",
        cell: (row) => (
          <Typography level="body-sm" className="cell-date">
            {row.created_at}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_amount"),
        selector: (row) => Number(row.amount) || 0,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <Typography component="span" className="cell-amount">
            {settings[5].value}{" "}
            {Number(row.amount || 0).toLocaleString("en-IN")}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_transaction_mode"),
        selector: (row) => row.transaction_mode,
        sortable: true,
        minWidth: "160px",
        cell: (row) => (
          <Typography
            level="body-sm"
            className="cell-transaction-mode"
            title={row.transaction_mode || "N/A"}
          >
            {row.transaction_mode || "N/A"}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_transaction_id"),
        selector: (row) => row.transaction_id,
        sortable: true,
        minWidth: "160px",
        cell: (row) => (
          <Typography
            level="body-sm"
            className="cell-transaction-id"
            title={row.transaction_id || "N/A"}
          >
            {row.transaction_id || "N/A"}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_transaction_message"),
        selector: (row) => row.transaction_message,
        sortable: true,
        minWidth: "180px",
        grow: 2,
        cell: (row) => (
          <Typography
            level="body-sm"
            className="cell-narration"
            title={row.transaction_message || "N/A"}
          >
            {row.transaction_message || "N/A"}
          </Typography>
        ),
      },
      {
        name: t("payouts_column_status"),
        selector: (row) => row.status,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <Chip
            variant="soft"
            color={getStatusColor(row.status)}
            size="sm"
            className="status-chip"
          >
            {row.status || "Unknown"}
          </Chip>
        ),
      },
    ],
    []
  );

  if (!props?.user?.auth_token) {
    return (
      <Box className="auth-loading">
        <CircularProgress size="sm" />
      </Box>
    );
  }

  const isWithdrawDisabled = () => {
    const minPayout = props?.settings?.find(
      (setting) => setting.key === "minPayoutDriverRecruiter"
    );

    if (!minPayout) return true;
    return (
      Number(props?.user?.wallet_balance || 0) < Number(minPayout.value || 0)
    );
  };

  useEffect(() => {
    setWithdrawDisabled(isWithdrawDisabled());
  }, []);

  return (
    <>
      {fullLoading && <Loading />}
      <Box className="payouts-container">
        <Card className="balance-card">
          <Card.Header className="balance-header">
            <Row className="align-items-center">
              <div className="col-12 col-md-6">
                <h5 className="balance-amount">
                  <span className="currency-symbol">{settings[5].value}</span>{" "}
                  {Number(props.user?.wallet_balance || 0).toLocaleString(
                    "th-TH"
                  )}
                </h5>
              </div>
            </Row>
          </Card.Header>

          <Card.Body className="balance-body">
            <div>
              <p className="balance-text">{t("payouts_current_earnings")}</p>
              {withdrawDisabled && (
                <small className="text-muted">
                  {t("payouts_below_limit_message")} {settings[5].value}{" "}
                  {Number(
                    props?.settings?.find(
                      (setting) => setting.key === "minPayoutDriverRecruiter"
                    ).value || 0
                  ).toLocaleString("en-IN")}
                </small>
              )}
            </div>
            <Button
              onClick={() => setWithdrawModalOpen(true)}
              disabled={withdrawDisabled}
              className={`withdraw-button ${
                withdrawDisabled ? "disabled" : ""
              }`}
              variant="solid"
            >
              {t("payouts_withdraw_btn")}
            </Button>
          </Card.Body>
        </Card>

        <Box className="header-section">
          <Typography level="h4" className="page-title">
            {t("sidebar_payouts")}
          </Typography>
          <Box className="controls-section">
            <CustomDateRangePicker
              dates={dates}
              setDates={setDates}
              onApply={handleApplyDateRange}
            />
            <Input
              size="sm"
              placeholder={t("common_search")}
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <Box className="export-buttons-container">
              <Button
                size="sm"
                variant="soft"
                onClick={() => exportToCsv(filteredRows, "payouts")}
                disabled={total === 0}
                startDecorator={<FileDownload className="export-icon" />}
                className="export-btn csv-btn"
              >
                {t("common_csv")}
              </Button>
              <Button
                size="sm"
                variant="soft"
                onClick={() =>
                  downloadTablePdf(columns, filteredRows, "payouts")
                }
                disabled={total === 0}
                startDecorator={<PictureAsPdf className="export-icon" />}
                className="export-btn pdf-btn"
              >
                {t("common_pdf")}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box className="table-container">
          <Paper elevation={0} className="table-paper">
            <div id="printable-table">
              <DataTable
                columns={columns}
                data={filteredRows}
                paginationTotalRows={total}
                paginationServer
                progressPending={loading}
                progressComponent={<LoadingComponent />}
                noDataComponent={<NoDataComponent />}
                pagination
                paginationPerPage={perPage}
                onChangeRowsPerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                responsive
                striped
                highlightOnHover
                customStyles={datatable_style}
                paginationComponentOptions={{
                  rowsPerPageText: t("common_rows_per_page"),
                  rangeSeparatorText: t("common_dt_separator"),
                  selectAllRowsItem: true,
                  selectAllRowsItemText: t("common_all"),
                }}
                dense={false}
              />
            </div>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        className="withdraw-modal"
      >
        <Box className="modal-content">
          <Box className="modal-header">
            <Box className="modal-icon">
              <Typography className="currency-symbol-light">
                {settings[5].value}
              </Typography>
            </Box>
            <Box className="modal-title-text">
              <Typography level="h4" className="modal-title">
                {t("payout_withdraw_title")}
              </Typography>
              <Typography level="body-sm" className="modal-subtitle">
                {t("payout_withdraw_subtitle")}
              </Typography>
            </Box>
          </Box>

          <Box className="modal-body">
            <Box className="amount-display">
              <Typography level="body-sm" className="amount-label">
                {t("payout_withdraw_amount")}
              </Typography>
              <Typography level="h3" className="amount-value">
                <span className="currency-symbol">{settings[5].value}</span>{" "}
                <span>
                  {Number(props.user?.wallet_balance || 0).toLocaleString(
                    "en-IN"
                  )}
                </span>
              </Typography>
              <Box className="availability-badge">
                <Typography level="body-sm" className="availability-text">
                  {t("payout_withdraw_available_for_withdrawal")}
                </Typography>
              </Box>
            </Box>

            <Box className="info-section">
              <Box className="info-icon">
                <Typography className="info-symbol">i</Typography>
              </Box>
              <Typography level="body-sm" className="info-text">
                {t("payout_withdraw_info_text")}
              </Typography>
            </Box>

            <Box className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setWithdrawModalOpen(false)}
              >
                {t("common_cancel")}
              </button>
              <button className="confirm-button" onClick={handleWithdraw}>
                {t("common_confirm")}
              </button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});

const mapDispatchToProps = {
  updateUserInfo,
};

export default connect(mapStateToProps, mapDispatchToProps)(Payouts);
